import "../../admin/list/styles.css";
import "./styles.css";

import {
  Button,
  Checkbox,
  Col,
  Form,
  notification,
  Row,
  Select,
  TimePicker,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { LoadingOutlined } from "@ant-design/icons";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { serviceTypeListAsync } from "../../../features/serviceType/serviceTypeListSlice";
import { getUserById } from "../../../services/adminUserApi";
import {
  CreateUserPrivilege,
  GetUserPrivileges,
  UpdateUserPrivilege,
} from "../../../services/userPermissionApi";
import {
  UserAuthorizedCountriesDescription,
  UserAuthorizedScheduleFromDescription,
  UserAuthorizedScheduleToDescription,
  UserAuthorizedServiceTypesDescription,
  UserAuthorizedWeekdayDescription,
} from "../../../shared/constants/Resources";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import UserDTO from "../../../shared/dtos/userDTO";
import UserPermissionDTO from "../../../shared/dtos/userPermissionDTO";
import { COUNTRIES } from "../../../utils/constants/countries";
import { USER_PERMISSION_TYPE } from "../../../utils/constants/userPermissionType";
import { selectCurrentAdminId } from "../../../utils/helpers/selectorHelper";
import ContainerLayout from "../../common/containerLayout";

const { Title } = Typography;

interface FormValues {
  name: string[];
  value: Array<Number> | dayjs.Dayjs;
}

const DATE_FORMAT = "HH:mm";
const STARTING_HOUR = "00:00";

export const EditUserPrivilege = () => {
  let { userId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const adminId = selectCurrentAdminId();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<UserDTO>();
  const [userPrivileges, setUserPrivileges] = useState<
    Array<UserPermissionDTO>
  >(new Array<UserPermissionDTO>());
  const serviceTypes = useAppSelector((x) => x.serviceTypeList.value);

  useEffect(() => {
    const getServiceTypes = async () => {
      if (serviceTypes.length) {
        await retriveUserPrivileges();
        return;
      }

      await dispatch(serviceTypeListAsync());
      await retriveUserPrivileges();
    };
    const getUser = async () => {
      const user = await getUserById(Number(userId));
      setUser(user);
      await getServiceTypes();
    };
    const retriveUserPrivileges = async () => {
      const userPrivileges: Array<UserPermissionDTO> = await GetUserPrivileges(
        Number(userId),
      );
      setUserPrivileges(userPrivileges);
      const formFields: Array<FormValues> = [
        {
          name: [UserAuthorizedCountriesDescription],
          value: new Array<number>(),
        },
        {
          name: [UserAuthorizedServiceTypesDescription],
          value: new Array<number>(),
        },
        {
          name: [UserAuthorizedScheduleFromDescription],
          value: dayjs(STARTING_HOUR, DATE_FORMAT),
        },
        {
          name: [UserAuthorizedScheduleToDescription],
          value: dayjs(STARTING_HOUR, DATE_FORMAT),
        },
        {
          name: [UserAuthorizedWeekdayDescription],
          value: new Array<number>(),
        },
      ];

      if (userPrivileges) {
        for (const userPrivilege of userPrivileges) {
          switch (userPrivilege.permissionType) {
            case USER_PERMISSION_TYPE.Country:
              if (userPrivilege.permissions) {
                const permissions = JSON.parse(userPrivilege.permissions);
                if (permissions) {
                  const permissionToBeAdded = new Array<number>();
                  for (const permission of permissions) {
                    const country = COUNTRIES.find(
                      (x) => x.value === permission,
                    );
                    if (country) {
                      permissionToBeAdded.push(country.value);
                    }
                  }
                  formFields.push({
                    name: [UserAuthorizedCountriesDescription],
                    value: permissionToBeAdded,
                  });
                }
              }
              break;
            case USER_PERMISSION_TYPE.Schedule:
              if (userPrivilege.permissions) {
                const permissions = JSON.parse(userPrivilege.permissions);
                if (permissions[0]) {
                  formFields.push({
                    name: [UserAuthorizedScheduleFromDescription],
                    value: dayjs(permissions[0], DATE_FORMAT),
                  });
                }
                if (permissions[1]) {
                  formFields.push({
                    name: [UserAuthorizedScheduleToDescription],
                    value: dayjs(permissions[1], DATE_FORMAT),
                  });
                }
              }
              break;
            case USER_PERMISSION_TYPE.ServiceType:
              if (userPrivilege.permissions) {
                const permissions = JSON.parse(userPrivilege.permissions);
                if (permissions) {
                  const permissionToBeAdded = new Array<number>();
                  for (const permission of permissions) {
                    const serviceType = serviceTypes.find(
                      (x) => x.id === permission,
                    );
                    if (serviceType) {
                      permissionToBeAdded.push(serviceType.id!);
                    }
                  }
                  formFields.push({
                    name: [UserAuthorizedServiceTypesDescription],
                    value: permissionToBeAdded,
                  });
                }
              }
              break;
            case USER_PERMISSION_TYPE.Weekday:
              if (userPrivilege.permissions) {
                const permissions: Array<number> = JSON.parse(
                  userPrivilege.permissions,
                );
                if (permissions) {
                  formFields.push({
                    name: [UserAuthorizedWeekdayDescription],
                    value: permissions,
                  });
                }
              }
              break;
            default:
              break;
          }
        }
      }

      form.setFields(formFields);
      setIsLoading(false);
    };
    getUser();
  }, [dispatch, form, serviceTypes, userId]);

  const onFinish = async (values: any) => {
    const {
      userAuthorizedCountries,
      userAuthorizedServiceTypes,
      userAuthorizedScheduleFrom,
      userAuthorizedScheduleTo,
      userAuthorizedWeekday,
    } = values;
    let missingPrivileges = [
      USER_PERMISSION_TYPE.Country,
      USER_PERMISSION_TYPE.Schedule,
      USER_PERMISSION_TYPE.ServiceType,
      USER_PERMISSION_TYPE.Weekday,
    ];

    try {
      if (userPrivileges.length) {
        for (const userPrivilege of userPrivileges) {
          switch (userPrivilege.permissionType) {
            case USER_PERMISSION_TYPE.Country:
              if (userAuthorizedCountries) {
                if (userPrivilege) {
                  missingPrivileges = missingPrivileges.filter(
                    (x) => x !== USER_PERMISSION_TYPE.Country,
                  );
                  const permission = JSON.stringify(userAuthorizedCountries);
                  userPrivilege.permissions = permission;
                  userPrivilege.updatedAt = new Date();
                  await UpdateUserPrivilege(userPrivilege.id!, userPrivilege);
                }
              }
              break;
            case USER_PERMISSION_TYPE.Schedule:
              if (userAuthorizedScheduleFrom && userAuthorizedScheduleTo) {
                if (userPrivilege) {
                  missingPrivileges = missingPrivileges.filter(
                    (x) => x !== USER_PERMISSION_TYPE.Schedule,
                  );
                  const userSchedule = [
                    dayjs(userAuthorizedScheduleFrom, DATE_FORMAT).format(
                      DATE_FORMAT,
                    ),
                    dayjs(userAuthorizedScheduleTo, DATE_FORMAT).format(
                      DATE_FORMAT,
                    ),
                  ];
                  const permission = JSON.stringify(userSchedule);
                  userPrivilege.permissions = permission;
                  userPrivilege.updatedAt = new Date();
                  await UpdateUserPrivilege(userPrivilege.id!, userPrivilege);
                }
              }
              break;
            case USER_PERMISSION_TYPE.ServiceType:
              if (userAuthorizedServiceTypes) {
                if (userPrivilege) {
                  missingPrivileges = missingPrivileges.filter(
                    (x) => x !== USER_PERMISSION_TYPE.ServiceType,
                  );
                  const permission = JSON.stringify(userAuthorizedServiceTypes);
                  userPrivilege.permissions = permission;
                  userPrivilege.updatedAt = new Date();
                  await UpdateUserPrivilege(userPrivilege.id!, userPrivilege);
                }
              }
              break;
            case USER_PERMISSION_TYPE.Weekday:
              if (userAuthorizedWeekday) {
                if (userPrivilege) {
                  missingPrivileges = missingPrivileges.filter(
                    (x) => x !== USER_PERMISSION_TYPE.Weekday,
                  );
                  const permission = JSON.stringify(userAuthorizedWeekday);
                  userPrivilege.permissions = permission;
                  userPrivilege.updatedAt = new Date();
                  await UpdateUserPrivilege(userPrivilege.id!, userPrivilege);
                }
              }
              break;
            default:
              break;
          }
        }
      }

      if (missingPrivileges) {
        for (const missingPrivilege of missingPrivileges) {
          switch (missingPrivilege) {
            case USER_PERMISSION_TYPE.Country:
              if (userAuthorizedCountries) {
                const userPrivilege = new UserPermissionDTO();
                const permission = JSON.stringify(userAuthorizedCountries);
                userPrivilege.permissions = permission;
                userPrivilege.userId = Number(userId);
                userPrivilege.adminId = adminId;
                userPrivilege.permissionType = USER_PERMISSION_TYPE.Country;
                userPrivilege.createdAt = new Date();
                await CreateUserPrivilege(userPrivilege);
              }
              break;
            case USER_PERMISSION_TYPE.ServiceType:
              if (userAuthorizedServiceTypes) {
                const userPrivilege = new UserPermissionDTO();
                const permission = JSON.stringify(userAuthorizedServiceTypes);
                userPrivilege.permissions = permission;
                userPrivilege.userId = Number(userId);
                userPrivilege.adminId = adminId;
                userPrivilege.permissionType = USER_PERMISSION_TYPE.ServiceType;
                userPrivilege.createdAt = new Date();
                await CreateUserPrivilege(userPrivilege);
              }
              break;
            case USER_PERMISSION_TYPE.Weekday:
              if (userAuthorizedWeekday) {
                const userPrivilege = new UserPermissionDTO();
                const permission = JSON.stringify(userAuthorizedWeekday);
                userPrivilege.permissions = permission;
                userPrivilege.userId = Number(userId);
                userPrivilege.adminId = adminId;
                userPrivilege.permissionType = USER_PERMISSION_TYPE.Weekday;
                userPrivilege.createdAt = new Date();
                await CreateUserPrivilege(userPrivilege);
              }
              break;
            case USER_PERMISSION_TYPE.Schedule:
              if (userAuthorizedScheduleFrom && userAuthorizedScheduleTo) {
                const userPrivilege = new UserPermissionDTO();
                const userSchedule = [
                  dayjs(userAuthorizedScheduleFrom, DATE_FORMAT).format(
                    DATE_FORMAT,
                  ),
                  dayjs(userAuthorizedScheduleTo, DATE_FORMAT).format(
                    DATE_FORMAT,
                  ),
                ];
                const permission = JSON.stringify(userSchedule);
                userPrivilege.permissions = permission;
                userPrivilege.userId = Number(userId);
                userPrivilege.adminId = adminId;
                userPrivilege.permissionType = USER_PERMISSION_TYPE.Schedule;
                userPrivilege.createdAt = new Date();
                await CreateUserPrivilege(userPrivilege);
              }
              break;
            default:
              break;
          }
        }
      }
      notification.success({
        message: "Se modificaron los permisos correctamente",
        description: `los permisos del usuario ${user?.firstName ?? ""} ${
          user?.lastName ?? ""
        } fueron modificados exitosamente`,
        placement: "bottom",
        key: "global",
      });
    } catch (e) {
      notification.error({
        message: `Ha ocurrido un error al intentar modificar los permisos del usuario ${
          user?.firstName ?? ""
        } ${user?.lastName ?? ""}`,
        placement: "bottom",
        key: "global",
      });
    }
    form.resetFields();

    navigate(WebRoutes.UserTableRoute, {
      replace: true,
    });
  };

  return (
    <ContainerLayout
      title={`Modificando permisos de ${user?.firstName ?? ""} ${
        user?.lastName ?? ""
      }`}
      showNotification={false}
      justify={"center"}
    >
      {isLoading && (
        <Col span={24} className="loading-container error-container">
          <LoadingOutlined />
        </Col>
      )}
      {!isLoading && (
        <Col span={12}>
          <Title level={3}>Información de los permisos del usuario</Title>
          <Title level={5} type="secondary">
            Por defecto, el usuario tiene autorizado pedir viajes en cualquier
            lugar, a cualquier dia y cualquier hora
          </Title>
          <Form
            layout="vertical"
            form={form}
            name="userPermissions"
            onFinish={onFinish}
            scrollToFirstError
          >
            <Row justify="center">
              <Col span={15}>
                <Form.Item
                  name={UserAuthorizedCountriesDescription}
                  label="Paises autorizados"
                  tooltip="Cuales paises tiene autorizados?"
                >
                  <Select
                    mode="multiple"
                    placeholder="Por favor seleccionar los paises"
                    options={COUNTRIES}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row justify="center">
              <Col span={15}>
                <Form.Item
                  name={UserAuthorizedServiceTypesDescription}
                  label="Tipos de servicio autorizados"
                  tooltip="Cuales tipos de servicios tiene autorizado?"
                >
                  <Select
                    mode="multiple"
                    placeholder="Por favor seleccionar los tipos de servicios"
                    options={serviceTypes.map((x) => ({
                      value: x.id,
                      label: x.name,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row justify="center">
              <Col span={15}>
                <Form.Item
                  name={UserAuthorizedWeekdayDescription}
                  label="Dias autorizados"
                  tooltip="Cuales dias de la semana tiene autorizado?"
                >
                  <Checkbox.Group
                    style={{
                      width: "100%",
                    }}
                  >
                    <Checkbox value="0">Domingo</Checkbox>
                    <Checkbox value="1">Lunes</Checkbox>
                    <Checkbox value="2">Martes</Checkbox>
                    <Checkbox value="3">Miercoles</Checkbox>
                    <Checkbox value="4">Jueves</Checkbox>
                    <Checkbox value="5">Viernes</Checkbox>
                    <Checkbox value="6">Sabado</Checkbox>
                  </Checkbox.Group>
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Form.Item
                label="Horario autorizado"
                tooltip="Cual es su horario deseado de servicio?"
                style={{ width: "100%" }}
              >
                <Row justify="space-evenly">
                  <Col>
                    <Form.Item
                      label="Hora Inicial"
                      tooltip="Cual desea que sea la primera hora permitida?"
                      name={UserAuthorizedScheduleFromDescription}
                    >
                      <TimePicker style={{ width: "98%" }} />
                    </Form.Item>
                  </Col>
                  <Col>
                    <Form.Item
                      label="Hora Final"
                      tooltip="Cual desea que sea la ultima hora permitida?"
                      name={UserAuthorizedScheduleToDescription}
                    >
                      <TimePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </Row>
            <Row justify="space-evenly">
              <Button type="primary" htmlType="submit">
                Guardar cambios
              </Button>
              <Button
                onClick={() =>
                  navigate(WebRoutes.UserTableRoute, {
                    replace: true,
                  })
                }
              >
                Cancelar
              </Button>
            </Row>
          </Form>
        </Col>
      )}
    </ContainerLayout>
  );
};

export default EditUserPrivilege;
